import { HttpClient, HttpContext, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { catchError, map, Observable, throwError } from 'rxjs';
import { environment } from 'src/environments/environment';
import type {
  AiCapability, AiChatResponse, AiSessionListResponse, AiSessionResponse, AiUploadResponse, AiUsageMeResponse,
} from 'src/app/api-client';
import { TOLERATE_FAILURE } from 'src/app/interceptors/tolerate-failure.token';
import { expectStatuses } from 'src/app/interceptors/expected-statuses.token';
import { httpErrorMessage } from 'src/app/utils/notify-http-error';

/** 400 = limits/validation, 409 = still answering, 429 = daily AI chat cap (P117) — answers for the user, not defects. */
const CHAT_REFUSALS = [400, 409, 429] as const;
const SESSION_EDIT_OUTCOMES = [400, 404] as const;

export type AiSurface = 'Web' | 'QuickEncode';

export interface AiSendRequest {
  /** null starts a new chat. */
  sessionId: string | null;
  message: string;
  attachmentIds: string[];
  surface: AiSurface;
  /** Case the user is looking at; only honoured by the server on a NEW chat. */
  pageCaseId: number | null;
}

/** The server refused with a readable reason: 409 = still answering, 400 = limits/validation, 429 = daily cap. */
export class AiChatRefusedError extends Error {
  constructor(message: string, readonly status: number) {
    super(message);
  }
}

@Injectable({ providedIn: 'root' })
export class AiAssistantService {
  private readonly base = `${environment.CalystaApiBaseURL}api/AiAssistant`;

  constructor(private http: HttpClient) {}

  send(req: AiSendRequest): Observable<AiChatResponse> {
    const body = {
      SessionId: req.sessionId,
      Message: req.message,
      AttachmentIds: req.attachmentIds,
      Surface: req.surface,
      PageContext: req.sessionId == null && req.pageCaseId != null ? { CaseId: req.pageCaseId } : null,
    };
    return this.http
      .post<AiChatResponse>(`${this.base}/Chat`, body, { context: expectStatuses(CHAT_REFUSALS) })
      .pipe(catchError((e) => throwError(() => toRefusal(e))));
  }

  upload(file: File): Observable<AiUploadResponse> {
    const form = new FormData();
    form.append('File', file, file.name);
    return this.http
      .post<AiUploadResponse>(`${this.base}/Attachments`, form, { context: expectStatuses([400]) })
      .pipe(catchError((e) => throwError(() => toRefusal(e))));
  }

  /** 403 = the user has no Employee row (guest): callers treat it as "Crystal AI not available". */
  listSessions(search: string, page = 1, pageSize = 30): Observable<AiSessionListResponse> {
    let params = new HttpParams().set('Page', page).set('PageSize', pageSize);
    if (search.trim()) params = params.set('Search', search.trim());
    return this.http.get<AiSessionListResponse>(`${this.base}/Sessions`, { params, context: expectStatuses([403]) });
  }

  /** 404 (deleted, or not the caller's chat) is an expected outcome — callers show "not found". */
  getSession(id: string): Observable<AiSessionResponse> {
    return this.http.get<AiSessionResponse>(`${this.base}/Sessions/${id}`, { context: expectStatuses([404]) });
  }

  /**
   * Session PATCH/DELETE: 400 (case not found, nothing to update) and 404 (chat gone) are expected —
   * callers show the server's message (httpErrorMessage) and resync.
   */
  rename(id: string, title: string): Observable<unknown> {
    return this.http.patch(`${this.base}/Sessions/${id}`, { Title: title }, { context: expectStatuses(SESSION_EDIT_OUTCOMES) });
  }

  setCase(id: string, caseId: number): Observable<unknown> {
    return this.http.patch(`${this.base}/Sessions/${id}`, { CaseId: caseId }, { context: expectStatuses(SESSION_EDIT_OUTCOMES) });
  }

  clearCase(id: string): Observable<unknown> {
    return this.http.patch(`${this.base}/Sessions/${id}`, { ClearCase: true }, { context: expectStatuses(SESSION_EDIT_OUTCOMES) });
  }

  deleteSession(id: string): Observable<unknown> {
    return this.http.delete(`${this.base}/Sessions/${id}`, { context: expectStatuses(SESSION_EDIT_OUTCOMES) });
  }

  getCapabilities(): Observable<AiCapability[]> {
    return this.http
      .get<{ Capabilities?: AiCapability[] }>(`${this.base}/Capabilities`)
      .pipe(map((r) => r.Capabilities ?? []));
  }

  /** Best effort (Home impact line) — a failure just hides the line. */
  getUsageMe(): Observable<AiUsageMeResponse> {
    return this.http.get<AiUsageMeResponse>(`${this.base}/Usage/Me`, {
      context: new HttpContext().set(TOLERATE_FAILURE, true),
    });
  }
}

function toRefusal(err: unknown): unknown {
  if (err instanceof HttpErrorResponse && (err.status === 400 || err.status === 409 || err.status === 429)) {
    return new AiChatRefusedError(httpErrorMessage(err, 'Crystal AI could not process this request.'), err.status);
  }
  return err;
}
